import type {
	CapturedNetworkRequest,
	CaptureResult,
	PostHogConfig,
} from "posthog-js";
import posthog from "posthog-js";
import {
	type PropsWithChildren,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";

import { useSession } from "@/hooks/auth-hooks";

const posthogToken =
	import.meta.env.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN ||
	import.meta.env.VITE_POSTHOG_PROJECT_TOKEN ||
	import.meta.env.VITE_PUBLIC_POSTHOG_TOKEN ||
	import.meta.env.VITE_POSTHOG_TOKEN;

const posthogHost =
	import.meta.env.VITE_PUBLIC_POSTHOG_HOST ||
	import.meta.env.VITE_POSTHOG_HOST ||
	"https://eu.i.posthog.com";

const sensitivePathPatterns = [
	[/^\/reset-password\/?/, "/reset-password"],
] as const;

function sanitizePath(pathname: string) {
	return sensitivePathPatterns.reduce(
		(path, [pattern, replacement]) => path.replace(pattern, replacement),
		pathname,
	);
}

function sanitizeUrl(value: unknown) {
	if (typeof value !== "string" || value.length === 0) return value;

	try {
		const url = new URL(
			value,
			typeof window === "undefined"
				? "https://agentmrr.com"
				: window.location.origin,
		);

		if (!value.startsWith("http") && !value.startsWith("/")) return value;

		const sanitizedPath = sanitizePath(url.pathname);
		return value.startsWith("http")
			? `${url.origin}${sanitizedPath}`
			: sanitizedPath;
	} catch {
		return value;
	}
}

function sanitizePostHogEvent(event: CaptureResult | null) {
	if (!event?.properties) return event;

	for (const propertyName of [
		"$current_url",
		"$pathname",
		"$referrer",
		"$referring_domain",
		"$prev_pageview_pathname",
		"$prev_pageview_last_scroll",
	]) {
		event.properties[propertyName] = sanitizeUrl(
			event.properties[propertyName],
		);
	}

	return event;
}

function sanitizeNetworkRequest(request: CapturedNetworkRequest) {
	const sanitizedName = sanitizeUrl(request.name);
	if (typeof sanitizedName === "string") {
		request.name = sanitizedName;
	}

	request.requestHeaders = undefined;
	request.responseHeaders = undefined;
	request.requestBody = undefined;
	request.responseBody = undefined;

	return request;
}

function PostHogUserIdentifier({ enabled }: { enabled: boolean }) {
	const { isPending, user } = useSession();
	const identifiedUserIdRef = useRef<string | null>(null);

	useEffect(() => {
		if (!enabled || isPending) return;

		if (!user?.id) {
			if (identifiedUserIdRef.current) {
				posthog?.reset();
				identifiedUserIdRef.current = null;
			}
			return;
		}

		if (identifiedUserIdRef.current === user.id) return;

		posthog?.identify(user.id, {
			email: user.email || undefined,
			name: user.name || undefined,
		});
		identifiedUserIdRef.current = user.id;
	}, [enabled, isPending, user?.email, user?.id, user?.name]);

	return null;
}

function PostHogPageviewCapture() {
	useEffect(() => {
		const capturePageview = () => posthog?.capture("$pageview");
		const originalPushState = window.history.pushState;
		const originalReplaceState = window.history.replaceState;
		const initialCaptureId = window.setTimeout(capturePageview, 1000);

		window.history.pushState = function pushState(...args) {
			const result = originalPushState.apply(this, args);
			capturePageview();
			return result;
		};
		window.history.replaceState = function replaceState(...args) {
			const result = originalReplaceState.apply(this, args);
			capturePageview();
			return result;
		};
		window.addEventListener("popstate", capturePageview);

		return () => {
			window.clearTimeout(initialCaptureId);
			window.history.pushState = originalPushState;
			window.history.replaceState = originalReplaceState;
			window.removeEventListener("popstate", capturePageview);
		};
	}, []);

	return null;
}

export function PostHogAnalyticsProvider({ children }: PropsWithChildren) {
	const [isLoaded, setIsLoaded] = useState(false);

	const options = useMemo<Partial<PostHogConfig>>(
		() => ({
			api_host: posthogHost,
			before_send: sanitizePostHogEvent,
			capture_pageview: false,
			defaults: "2026-01-30",
			disable_session_recording: false,
			loaded: () => setIsLoaded(true),
			person_profiles: "always",
			session_recording: {
				maskAllInputs: true,
				maskCapturedNetworkRequestFn: sanitizeNetworkRequest,
			},
		}),
		[],
	);

	useEffect(() => {
		if (!posthogToken) return;

		if (posthog.__loaded) {
			setIsLoaded(true);
			return;
		}

		posthog.init(posthogToken, options);
	}, [options]);

	if (!posthogToken) {
		return <>{children}</>;
	}

	return (
		<>
			<PostHogUserIdentifier enabled={isLoaded} />
			<PostHogPageviewCapture />
			{children}
		</>
	);
}
